import { createAsyncThunk } from "@reduxjs/toolkit";
import {
    getAllPropertiesByCaseId,
    getPropertyById,
    createProperty,
    updateProperty,
    deleteProperty,
    searchProperties,
    bulkCreateProperties,
    getPropertiesWithPagination,
    exportPropertiesToExcel,
    formatPropertyData,
    parseAttachmentIds,
    validatePropertyData
} from "../../../api/casePropertyDetailsAsset_api";

// =========== GET ALL PROPERTIES BY CASE ID ===========
export const getAllPropertiesByCaseIdAction = createAsyncThunk(
    "property/getAllPropertiesByCaseId",
    async (caseDetailsID, { rejectWithValue }) => {
        try {
            const result = await getAllPropertiesByCaseId(caseDetailsID);

            if (result.success) {
                return {
                    caseDetailsID,
                    properties: result.data,
                    assetOwnedBy: result.assetOwnedBy,
                    count: result.count,
                    message: result.message
                };
            } else {
                return rejectWithValue({
                    message: result.message,
                    error: result.error
                });
            }
        } catch (error) {
            return rejectWithValue({
                message: "Failed to fetch properties",
                error: error.message
            });
        }
    }
);

// =========== GET SINGLE PROPERTY BY ID ===========
export const getPropertyByIdAction = createAsyncThunk(
    "property/getPropertyById",
    async (propertyID, { rejectWithValue }) => {
        try {
            const result = await getPropertyById(propertyID);

            if (result.success) {
                return {
                    property: result.data,
                    message: result.message
                };
            } else {
                return rejectWithValue({
                    message: result.message,
                    error: result.error
                });
            }
        } catch (error) {
            return rejectWithValue({
                message: "Failed to fetch property",
                error: error.message
            });
        }
    }
);

// =========== CREATE PROPERTY ===========
export const createPropertyAction = createAsyncThunk(
    "property/createProperty",
    async ({ caseDetailsID, formData }, { rejectWithValue }) => {
        try {
            let dataToSend;


            if (formData instanceof FormData) {
                dataToSend = formData;

                if (!dataToSend.has('caseDetailsID')) {
                    dataToSend.append('caseDetailsID', caseDetailsID);
                }
            } else {

                dataToSend = formatPropertyData({
                    ...formData,
                    caseDetailsID: caseDetailsID || formData.caseDetailsID
                });
            }

            const result = await createProperty(dataToSend);

            if (result.success) {
                return {
                    property: result.data,
                    message: result.message
                };
            } else {
                return rejectWithValue({
                    message: result.message,
                    error: result.error
                });
            }
        } catch (error) {
            return rejectWithValue({
                message: "Failed to create property",
                error: error.message
            });
        }
    }
);

// =========== UPDATE PROPERTY ===========
export const updatePropertyAction = createAsyncThunk(
    "property/updateProperty",
    async ({ propertyID, propertyData, caseDetailsID }, { rejectWithValue }) => {
        try {
            let dataToSend;

            if (propertyData instanceof FormData) {
                dataToSend = propertyData;
                if (!dataToSend.has('caseDetailsID') && caseDetailsID) {
                    dataToSend.append('caseDetailsID', caseDetailsID);
                }
            } else {

                dataToSend = formatPropertyData({
                    ...propertyData,
                    caseDetailsID,
                });
            }

            const result = await updateProperty(propertyID, dataToSend);


            if (result.success) {
                return {
                    propertyID,
                    property: result.data,
                    message: result.message
                };
            } else {
                return rejectWithValue({
                    message: result.message,
                    error: result.error
                });
            }
        } catch (error) {
            return rejectWithValue({
                message: "Failed to update property",
                error: error.message
            });
        }
    }
);

// =========== DELETE PROPERTY ===========
export const deletePropertyAction = createAsyncThunk(
    "property/deleteProperty",
    async ({ propertyID, updatedBy }, { rejectWithValue }) => {
        try {
            const result = await deleteProperty(propertyID, updatedBy);

            if (result.success) {
                return {
                    propertyID,
                    message: result.message
                };
            } else {
                return rejectWithValue({
                    message: result.message,
                    error: result.error
                });
            }
        } catch (error) {
            return rejectWithValue({
                message: "Failed to delete property",
                error: error.message
            });
        }
    }
);

// =========== SEARCH PROPERTIES ===========
export const searchPropertiesAction = createAsyncThunk(
    "property/searchProperties",
    async ({ caseDetailsID, searchCriteria }, { rejectWithValue }) => {
        try {
            const result = await searchProperties(caseDetailsID, searchCriteria);

            if (result.success) {
                return {
                    caseDetailsID,
                    properties: result.data,
                    count: result.count,
                    searchCriteria,
                    message: result.message
                };
            } else {
                return rejectWithValue({
                    message: result.message,
                    error: result.error
                });
            }
        } catch (error) {
            return rejectWithValue({
                message: "Failed to search properties",
                error: error.message
            });
        }
    }
);

// =========== BULK CREATE PROPERTIES ===========
export const bulkCreatePropertiesAction = createAsyncThunk(
    "property/bulkCreateProperties",
    async (propertiesData, { rejectWithValue }) => {
        try {

            const formattedProperties = propertiesData.map(property => formatPropertyData(property));


            for (const property of formattedProperties) {
                const validation = validatePropertyData(property);
                if (!validation.isValid) {
                    return rejectWithValue({
                        message: "Validation failed for one or more properties",
                        errors: validation.errors
                    });
                }
            }

            const result = await bulkCreateProperties(formattedProperties);

            if (result.success) {
                return {
                    properties: result.data,
                    message: result.message
                };
            } else {
                return rejectWithValue({
                    message: result.message,
                    error: result.error
                });
            }
        } catch (error) {
            return rejectWithValue({
                message: "Failed to bulk create properties",
                error: error.message
            });
        }
    }
);

// =========== GET PROPERTIES WITH PAGINATION ===========
export const getPropertiesWithPaginationAction = createAsyncThunk(
    "property/getPropertiesWithPagination",
    async ({ caseDetailsID, page = 1, limit = 10 }, { rejectWithValue }) => {
        try {
            const result = await getPropertiesWithPagination(caseDetailsID, page, limit);

            if (result.success) {
                return {
                    caseDetailsID,
                    properties: result.data,
                    pagination: result.pagination,
                    page,
                    limit,
                    message: result.message
                };
            } else {
                return rejectWithValue({
                    message: result.message,
                    error: result.error
                });
            }
        } catch (error) {
            return rejectWithValue({
                message: "Failed to fetch properties with pagination",
                error: error.message
            });
        }
    }
);

// =========== EXPORT PROPERTIES TO EXCEL ===========
export const exportPropertiesToExcelAction = createAsyncThunk(
    "property/exportPropertiesToExcel",
    async (caseDetailsID, { rejectWithValue }) => {
        try {
            const result = await exportPropertiesToExcel(caseDetailsID);

            if (result.success) {
                return {
                    caseDetailsID,
                    blobData: result.data,
                    message: result.message
                };
            } else {
                return rejectWithValue({
                    message: result.message,
                    error: result.error
                });
            }
        } catch (error) {
            return rejectWithValue({
                message: "Failed to export properties",
                error: error.message
            });
        }
    }
);

// =========== FORMAT PROPERTY DATA (Non-async utility action) ===========
export const formatPropertyDataAction = (propertyData) => ({
    type: "property/formatPropertyData",
    payload: formatPropertyData(propertyData)
});

// =========== PARSE ATTACHMENT IDs (Non-async utility action) ===========
export const parseAttachmentIdsAction = (attachmentField) => ({
    type: "property/parseAttachmentIds",
    payload: parseAttachmentIds(attachmentField)
});

// =========== VALIDATE PROPERTY DATA (Non-async utility action) ===========
export const validatePropertyDataAction = (propertyData) => ({
    type: "property/validatePropertyData",
    payload: validatePropertyData(propertyData)
});

// =========== CLEAR PROPERTY STATE ===========
export const clearPropertyStateAction = () => ({
    type: "property/clearState"
});

// =========== SET SELECTED PROPERTY ===========
export const setSelectedPropertyAction = (property) => ({
    type: "property/setSelectedProperty",
    payload: property
});

// =========== SET PROPERTY FILTERS ===========
export const setPropertyFiltersAction = (filters) => ({
    type: "property/setFilters",
    payload: filters
});
